/**
 * SAH schedule series domain helpers (recurring demand → materialised instances).
 * Pure — no I/O. Used by web series actions and contract tests.
 */

export type ScheduleSeriesRecurrencePattern =
  | 'WEEKLY'
  | 'FORTNIGHTLY_ODD'
  | 'FORTNIGHTLY_EVEN'
  | 'MONTHLY_NTH_DOW'
  | 'CUSTOM';

/** Product language freeze (RS-0 / D-08) — do not conflate these three. */
export const SCHEDULE_SERIES_PRODUCT_LANGUAGE = {
  /** SAH schedule_template_entries → materialised client visits */
  scheduleSeries: 'schedule series',
  /** RAC wing staffing patterns */
  facilityStaffingPattern: 'facility staffing pattern',
  /** Dispatch demand-band display grouping (not edit authority) */
  demandPattern: 'demand pattern',
} as const;

/** Materialise idempotency key for one SAH schedule entry occurrence. */
export function sahMaterialiseIdempotencyKey(
  scheduleTemplateEntryId: string,
  serviceDateYmd: string,
): string {
  const entry = String(scheduleTemplateEntryId ?? '').trim();
  const date = String(serviceDateYmd ?? '').trim();
  if (!entry || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    throw new Error('sah_materialise_idempotency_key_invalid');
  }
  return `hc:${entry}:${date}`;
}

/** Parse entry id + service date from an hc: materialise key. */
export function parseSahMaterialiseIdempotencyKey(
  key: string | null | undefined,
): { entryId: string; serviceDateYmd: string } | null {
  const text = String(key ?? '').trim();
  const match = /^hc:([0-9a-f-]{36}):(\d{4}-\d{2}-\d{2})$/i.exec(text);
  if (!match) return null;
  return { entryId: match[1]!, serviceDateYmd: match[2]! };
}

export type SeriesPropagateScope = 'THIS_ONLY' | 'THIS_AND_FUTURE' | 'ALL_OPEN';

export type SeriesInstanceCandidate = {
  slotId: string;
  shiftId: string;
  scheduleTemplateEntryId: string | null;
  /** Tenant-local service date YYYY-MM-DD when known */
  seriesInstanceDate: string | null;
  /** Slot/shift runtime status */
  status: string | null;
  parentStatus?: string | null;
  startTimeIso: string;
  /** Distinct client slots on the same shift (multi-service / multi-client) */
  slotCountOnShift?: number;
};

const TERMINAL_OR_LOCKED = new Set([
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
  'NO_SHOW',
  'INCOMPLETE',
]);

export type SeriesPropagateFilterResult = {
  update: SeriesInstanceCandidate[];
  skipped: Array<SeriesInstanceCandidate & { reason: string }>;
};

/**
 * Select open series instances eligible for window propagate.
 * Fail-closed on runtime-locked statuses and multi-slot shifts (RS-2 / D-07).
 */
export function filterSeriesInstancesForPropagate(input: {
  candidates: SeriesInstanceCandidate[];
  entryId: string;
  scope: SeriesPropagateScope;
  /** Tenant-local YYYY-MM-DD for THIS_ONLY or THIS_AND_FUTURE anchor */
  fromDateYmd: string;
}): SeriesPropagateFilterResult {
  const entryId = String(input.entryId ?? '').trim();
  const fromDate = String(input.fromDateYmd ?? '').trim();
  const update: SeriesInstanceCandidate[] = [];
  const skipped: Array<SeriesInstanceCandidate & { reason: string }> = [];

  for (const row of input.candidates) {
    if (!row.scheduleTemplateEntryId || row.scheduleTemplateEntryId !== entryId) {
      skipped.push({ ...row, reason: 'wrong_series' });
      continue;
    }
    const status = String(row.status ?? '').toUpperCase();
    const parent = String(row.parentStatus ?? '').toUpperCase();
    if (TERMINAL_OR_LOCKED.has(status) || TERMINAL_OR_LOCKED.has(parent)) {
      skipped.push({ ...row, reason: 'runtime_locked_or_terminal' });
      continue;
    }
    if ((row.slotCountOnShift ?? 1) > 1) {
      skipped.push({ ...row, reason: 'multi_slot_shift_skipped' });
      continue;
    }

    const instanceDate =
      row.seriesInstanceDate && /^\d{4}-\d{2}-\d{2}$/.test(row.seriesInstanceDate)
        ? row.seriesInstanceDate
        : row.startTimeIso.slice(0, 10);

    if (input.scope === 'THIS_ONLY') {
      if (instanceDate !== fromDate) {
        skipped.push({ ...row, reason: 'outside_this_only' });
        continue;
      }
    } else if (input.scope === 'THIS_AND_FUTURE') {
      if (instanceDate < fromDate) {
        skipped.push({ ...row, reason: 'before_from_date' });
        continue;
      }
    }
    // ALL_OPEN: no date filter beyond open status

    update.push(row);
  }

  return { update, skipped };
}

/**
 * Tenant-local service date for a series occurrence from a shift start instant.
 * Matches RS-0b stamp trigger semantics (tenant timezone, not bare UTC).
 */
export function seriesInstanceDateFromStartIso(startTimeIso: string, timeZone: string): string {
  const start = new Date(startTimeIso);
  if (Number.isNaN(start.getTime())) {
    throw new Error('series_instance_date_invalid_start');
  }
  const tz = timeZone?.trim() || 'Australia/Sydney';
  // en-CA yields YYYY-MM-DD in the target zone
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(start);
}

/** Validate schedule entry timing payload before controlled update. */
export function validateScheduleEntryDefinitionUpdate(input: {
  dayOfWeek?: number | null;
  startTime?: string | null;
  durationMinutes?: number | null;
  recurrencePattern?: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
}): { ok: true } | { ok: false; error: string; code: string } {
  if (input.dayOfWeek != null) {
    const d = Number(input.dayOfWeek);
    if (!Number.isInteger(d) || d < 0 || d > 6) {
      return { ok: false, error: 'Day of week must be 0–6.', code: 'invalid_day_of_week' };
    }
  }
  if (input.startTime != null && input.startTime !== '') {
    if (!/^\d{2}:\d{2}(:\d{2})?$/.test(String(input.startTime).trim())) {
      return { ok: false, error: 'Start time must be HH:MM.', code: 'invalid_start_time' };
    }
  }
  if (input.durationMinutes != null) {
    const m = Number(input.durationMinutes);
    if (!Number.isFinite(m) || m <= 0 || m > 24 * 60) {
      return {
        ok: false,
        error: 'Duration must be a positive number of minutes.',
        code: 'invalid_duration',
      };
    }
  }
  if (input.recurrencePattern != null && input.recurrencePattern !== '') {
    const allowed = new Set([
      'WEEKLY',
      'FORTNIGHTLY_ODD',
      'FORTNIGHTLY_EVEN',
      'MONTHLY_NTH_DOW',
      'CUSTOM',
    ]);
    if (!allowed.has(String(input.recurrencePattern).toUpperCase())) {
      return {
        ok: false,
        error: 'Unsupported recurrence pattern.',
        code: 'invalid_recurrence_pattern',
      };
    }
  }
  const from = input.effectiveFrom;
  const to = input.effectiveTo;
  if (from != null && from !== '' && !/^\d{4}-\d{2}-\d{2}$/.test(from)) {
    return {
      ok: false,
      error: 'effective_from must be YYYY-MM-DD.',
      code: 'invalid_effective_from',
    };
  }
  if (to != null && to !== '' && !/^\d{4}-\d{2}-\d{2}$/.test(to)) {
    return { ok: false, error: 'effective_to must be YYYY-MM-DD.', code: 'invalid_effective_to' };
  }
  if (from && to && to < from) {
    return {
      ok: false,
      error: 'effective_to cannot be before effective_from.',
      code: 'effective_range_inverted',
    };
  }
  return { ok: true };
}
